import { Box, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";

import { Agent } from "@/api/generated";
import theme from "@/theme";
import { PlannerCarouselPosition } from "@/types/plannerCarouselPosition";

import { FeaturedPlannerCard } from "./PlannerCards";
import { PLANNER_MOTION } from "./plannerMotion";
import { PlannerReveal } from "./PlannerReveal";

const useStyles = makeStyles(() => ({
  rail: {
    position: "relative", display: "flex", alignItems: "stretch", gap: 12, padding: "0 24px 8px", overflowX: "auto",
    scrollSnapType: "x mandatory", scrollPaddingInline: "24px", overscrollBehaviorX: "contain", scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
    "& > article": { flex: "0 0 100%", width: "100%", maxWidth: 342 },
    "&:focus-visible": { outline: `2px solid ${theme.palette.semantic["border/focus"]}`, outlineOffset: -2 },
  },
  footer: { margin: "8px 24px 0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  dots: { display: "flex", height: 18, alignItems: "center" },
  dotButton: { width: 16, height: 28, border: 0, padding: 0, display: "grid", placeItems: "center", background: "transparent", cursor: "pointer" },
  dot: { width: 8, height: 8, borderRadius: 9999, backgroundColor: theme.palette.semantic["border/default"] },
  selectedDot: { backgroundColor: theme.palette.semantic["action/primary"] },
  hint: { fontSize: 12, lineHeight: 1.5, color: theme.palette.semantic["text/secondary"] },
}));

const INITIAL_POSITION: PlannerCarouselPosition = { scrollLeft: 0 };
const noop = () => undefined;

/**
 * 上位3人の大判カードを横スワイプで切り替えるカルーセル。登録導線（診断結果）とプランナー一覧で共用する。
 * 登場の演出と、詳細から戻ったときの横位置の復元は登録導線だけが使うため、
 * animate / railRef / initialPosition / onPositionChange は省略可（省略時は演出なし・先頭のカードから表示）。
 */
export const RecommendedPlannerCarousel: React.FC<{
  agents: Agent[];
  animate?: boolean;
  reduced?: boolean;
  // ホームは相性グラフを省く
  showRadarChart?: boolean;
  railRef?: React.RefObject<HTMLDivElement>;
  initialPosition?: PlannerCarouselPosition;
  onPositionChange?: (position: PlannerCarouselPosition) => void;
  onDetail: (agent: Agent) => void;
}> = ({ agents, animate = false, reduced = false, showRadarChart = true, railRef: railRefProp, initialPosition = INITIAL_POSITION, onPositionChange = noop, onDetail }) => {
  const classes = useStyles();
  const ownRailRef = React.useRef<HTMLDivElement>(null);
  const railRef = railRefProp ?? ownRailRef;
  const [index, setIndex] = React.useState(0);
  const nearestIndex = (rail: HTMLDivElement) => {
    const cards = Array.from(rail.children) as HTMLElement[];
    return cards.reduce((nearest, card, i) =>
      Math.abs(card.offsetLeft - cards[0].offsetLeft - rail.scrollLeft) < Math.abs(cards[nearest].offsetLeft - cards[0].offsetLeft - rail.scrollLeft) ? i : nearest, 0);
  };
  React.useLayoutEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const restoredIndex = agents.findIndex(agent => agent.id === initialPosition.plannerId);
    const cards = Array.from(rail.children) as HTMLElement[];
    // 同じ並びなら移動途中の位置も復元。APIの並びが変わった場合は閲覧中だった人を優先する。
    const originalIndex = cards.length > 1 ? Math.round(initialPosition.scrollLeft / (cards[1].offsetLeft - cards[0].offsetLeft || 1)) : 0;
    rail.scrollLeft = restoredIndex >= 0 && restoredIndex !== originalIndex
      ? cards[restoredIndex].offsetLeft - cards[0].offsetLeft : initialPosition.scrollLeft;
    const selected = nearestIndex(rail);
    setIndex(selected);
    onPositionChange({ scrollLeft: rail.scrollLeft, plannerId: agents[selected]?.id });
  }, []);
  const goTo = (next: number) => {
    const rail = railRef.current;
    if (!rail) return;
    const target = Math.max(0, Math.min(agents.length - 1, next));
    const first = rail.firstElementChild as HTMLElement;
    const card = rail.children[target] as HTMLElement;
    setIndex(target);
    rail.scrollTo({ left: card.offsetLeft - first.offsetLeft, behavior: reduced ? "auto" : "smooth" });
  };
  return (
    <>
      <PlannerReveal delay={PLANNER_MOTION.featured} duration={PLANNER_MOTION.reveal} animate={animate && !reduced}>
        <div ref={railRef} className={classes.rail} role="region" aria-roledescription="カルーセル" aria-label="おすすめプランナー。左右にスワイプで切り替え" tabIndex={0}
          onScroll={event => {
            const selected = nearestIndex(event.currentTarget);
            setIndex(selected);
            onPositionChange({ scrollLeft: event.currentTarget.scrollLeft, plannerId: agents[selected]?.id });
          }}
          onKeyDown={event => {
            if (event.target !== event.currentTarget) return;
            const next = event.key === "ArrowRight" ? index + 1 : event.key === "ArrowLeft" ? index - 1 : event.key === "Home" ? 0 : event.key === "End" ? agents.length - 1 : null;
            if (next === null) return;
            event.preventDefault();
            goTo(next);
          }}>
          {agents.map((agent, i) => <FeaturedPlannerCard key={agent.id ?? `featured-${i}`} agent={agent} rank={i + 1} showRadarChart={showRadarChart} onDetail={() => onDetail(agent)} />)}
        </div>
      </PlannerReveal>
      {agents.length > 1 && <Box className={classes.footer}>
        <Box className={classes.dots} aria-label="カードの選択">
          {agents.map((agent, i) => <button type="button" key={agent.id ?? i} className={classes.dotButton} aria-label={`${i + 1}人目のおすすめを見る`} aria-current={index === i ? "true" : undefined} onClick={() => goTo(i)}>
            <span className={`${classes.dot} ${index === i ? classes.selectedDot : ""}`} />
          </button>)}
        </Box>
        <Typography className={classes.hint}>横にスワイプできます</Typography>
      </Box>}
    </>
  );
};
